import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { 
  User, Mail, Phone, MapPin, Calendar, Save, Loader2, Camera,
  Shield, Crown, Users, Bell, Wallet, FileText, Truck, Megaphone, ClipboardList
} from 'lucide-react';
import { toast } from 'sonner';
import ProfileImageUpload from '../components/ProfileImageUpload';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Committee icons mapping
const COMMITTEE_ICONS = {
  'executive': Shield,
  'finance': Wallet,
  'program': Calendar,
  'logistics': Truck,
  'communications': Megaphone,
  'documentation': FileText,
  'registration': ClipboardList,
  'sponsorship': Users
};

const COMMITTEE_COLORS = {
  'executive': 'bg-maroon text-white',
  'finance': 'bg-green-600 text-white',
  'program': 'bg-purple-600 text-white',
  'logistics': 'bg-blue-600 text-white',
  'communications': 'bg-orange-500 text-white',
  'documentation': 'bg-teal-600 text-white',
  'registration': 'bg-indigo-600 text-white',
  'sponsorship': 'bg-amber-600 text-white'
};

const Profile = () => {
  const { user, committees, permissions, updateUser, isOrganizer, isAttendee } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [formData, setFormData] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    batch_year: user?.batch_year || '',
    phone: user?.phone || '',
    address: user?.address || '',
    profile_photo: user?.profile_photo || ''
  });

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/notifications`);
      setNotifications(response.data.filter(n => !n.is_read).slice(0, 5));
    } catch (error) {
      console.error('Failed to fetch notifications');
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image size must be less than 2MB');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setFormData({ ...formData, profile_photo: reader.result });
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const updateData = {
        ...formData,
        batch_year: formData.batch_year ? parseInt(formData.batch_year) : null
      };
      const response = await axios.put(`${API_URL}/api/auth/profile`, updateData);
      updateUser(response.data.user || response.data);
      toast.success('Profile updated successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const markNotificationRead = async (notificationId) => {
    try {
      await axios.put(`${API_URL}/api/notifications/${notificationId}/read`);
      setNotifications(notifications.filter(n => n.id !== notificationId));
    } catch (error) {
      console.error('Failed to mark notification as read');
    }
  };

  const getInitials = (firstName, lastName) => {
    return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
  };

  const getCommitteeIcon = (committeeName) => {
    const name = committeeName.toLowerCase();
    for (const [key, Icon] of Object.entries(COMMITTEE_ICONS)) {
      if (name.includes(key)) return Icon;
    }
    return Users;
  };

  const getCommitteeColor = (committeeName) => {
    const name = committeeName.toLowerCase();
    for (const [key, color] of Object.entries(COMMITTEE_COLORS)) {
      if (name.includes(key)) return color;
    }
    return 'bg-gray-500 text-white';
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6 animate-fade-in" data-testid="profile-page">
      <div>
        <h1 className="font-heading text-3xl font-bold text-maroon flex items-center gap-3">
          <User className="h-8 w-8" />
          My Profile
        </h1>
        <p className="text-gray-600 mt-1">
          Manage your personal information
        </p>
      </div>

      {/* Notifications */}
      {notifications.length > 0 && (
        <Card className="border-0 shadow-sm bg-gradient-to-r from-amber-50 to-orange-50 border-l-4 border-l-amber-500">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2 text-amber-700">
              <Bell className="h-5 w-5" />
              Notifications ({notifications.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {notifications.map((notif) => (
                <div key={notif.id} className="flex items-center justify-between p-3 bg-white rounded-lg">
                  <div>
                    <p className="font-medium text-gray-900">{notif.title}</p>
                    <p className="text-sm text-gray-500">{notif.message}</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => markNotificationRead(notif.id)}
                    className="text-amber-600"
                  >
                    Dismiss
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Committee Roles Card - Only for Attendees */}
      {isAttendee && isAttendee() && committees && committees.length > 0 && (
        <Card className="border-0 shadow-sm bg-white" data-testid="committee-roles-card">
          <CardHeader>
            <CardTitle className="font-heading text-xl text-maroon flex items-center gap-2">
              <Crown className="h-5 w-5 text-gold" />
              My Committee Roles
            </CardTitle>
            <CardDescription>
              You have special permissions based on your committee assignments
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {committees.map((committee) => {
                const Icon = getCommitteeIcon(committee.name);
                const colorClass = getCommitteeColor(committee.name);
                return (
                  <div 
                    key={committee.id} 
                    className="flex items-center gap-4 p-4 rounded-xl border border-gray-200 hover:border-maroon/30 transition-colors"
                    data-testid={`committee-role-${committee.id}`}
                  >
                    <div className={`p-3 rounded-xl ${colorClass}`}>
                      <Icon className="h-6 w-6" />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-gray-900">{committee.name}</p>
                      <div className="flex items-center gap-2 mt-1">
                        {committee.is_head ? (
                          <Badge className="bg-gold text-cream-dark">
                            <Crown className="h-3 w-3 mr-1" />
                            Committee Head
                          </Badge>
                        ) : (
                          <Badge variant="outline">Member</Badge>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Permissions Summary */}
            {permissions && permissions.length > 0 && (
              <div className="mt-6 pt-6 border-t">
                <p className="text-sm font-medium text-gray-600 mb-3">Your Permissions:</p>
                <div className="flex flex-wrap gap-2">
                  {permissions.includes('all') ? (
                    <Badge className="bg-maroon text-white">Full Access (Executive)</Badge>
                  ) : (
                    permissions.map((perm) => (
                      <Badge key={perm} variant="outline" className="text-maroon border-maroon">
                        {perm.replace(/_/g, ' ')}
                      </Badge>
                    ))
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Profile Card */}
      <Card className="border-0 shadow-sm bg-white">
        <CardHeader className="text-center pb-6">
          <div className="relative inline-block mx-auto">
            <ProfileImageUpload user={user} onUploadComplete={() => window.location.reload()} />
          </div>
          <CardTitle className="font-heading text-2xl mt-4">
            {user?.first_name} {user?.last_name}
          </CardTitle>
          <div className="flex items-center justify-center gap-2 mt-2 flex-wrap">
            <Badge className={isOrganizer && isOrganizer() ? 'bg-maroon text-white' : 'bg-blue-500 text-white'}>
              {isOrganizer && isOrganizer() ? 'Organizer' : 'Attendee'}
            </Badge>
            {user?.batch_year && (
              <Badge variant="outline">Batch {user.batch_year}</Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="first_name">First Name</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    id="first_name"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleChange}
                    className="pl-10 bg-cream-paper"
                    required
                    data-testid="first-name-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="last_name">Last Name</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    id="last_name"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleChange}
                    className="pl-10 bg-cream-paper"
                    required
                    data-testid="last-name-input"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Email Address</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  value={user?.email}
                  className="pl-10 bg-gray-100"
                  disabled
                />
              </div>
              <p className="text-xs text-gray-500">Email cannot be changed</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="batch_year">Batch Year</Label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    id="batch_year"
                    name="batch_year"
                    type="number"
                    value={formData.batch_year}
                    onChange={handleChange}
                    className="pl-10 bg-cream-paper"
                    placeholder="1995"
                    min="1950"
                    max="2030"
                    data-testid="batch-year-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone Number</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="pl-10 bg-cream-paper"
                    placeholder="+63 917 xxx xxxx"
                    data-testid="phone-input"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="address">Address</Label>
              <div className="relative">
                <MapPin className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Textarea
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  className="pl-10 bg-cream-paper min-h-[80px]"
                  placeholder="Your current address"
                  data-testid="address-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full h-12 bg-maroon hover:bg-maroon-hover text-white rounded-full font-medium text-lg"
              disabled={loading}
              data-testid="save-profile-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-5 w-5 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default Profile;
